import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckSquare, XCircle, AlertTriangle, HelpCircle, Search, Timer,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ClaimsResult } from '../../../types/analysis-session';
import { HeadlineCard } from './HeadlineCard';
import { renderClaimsEditorial } from '../details/claims';
import { StatBlock, ChipsRow, Chip } from '../styles';
// isRo no longer used here; details receive `t` directly.

const credAccent = (s: number): string =>
  s >= 60 ? '#22c55e' : s >= 40 ? '#eab308' : s >= 20 ? '#f97316' : '#ef4444';

const credIcon = (s: number): LucideIcon => {
  if (s >= 60) return CheckSquare;
  if (s >= 40) return HelpCircle;
  if (s >= 20) return AlertTriangle;
  return XCircle;
};

interface Props {
  result: ClaimsResult;
}

export const ClaimsResults: React.FC<Props> = ({ result }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const rawCred = parseFloat(String(result.credibility_score)) || 0;
  const credPercent = Math.round(rawCred > 1 ? rawCred : rawCred * 100);
  const accent = credAccent(credPercent);
  const Icon = credIcon(credPercent);

  const buildTldr = (): string => {
    const total = result.total_claims || result.claims_verified.length || 0;
    if (total === 0) return t('analysis.claims.noVerifiableClaims');
    const parts: string[] = [];
    if (result.verified_true > 0) parts.push(`${result.verified_true} ${t('analysis.claims.shortTrue')}`);
    if (result.verified_false > 0) parts.push(`${result.verified_false} ${t('analysis.claims.shortFalse')}`);
    if (result.unverified > 0) parts.push(`${result.unverified} ${t('analysis.claims.shortUnverified')}`);
    if (result.opinions > 0) parts.push(`${result.opinions} ${t('analysis.claims.shortOpinions')}`);
    return t('analysis.claims.tldrSummary', {
      total,
      noun: t('analysis.claims.claim', { count: total }),
      parts: parts.join(', '),
      percent: credPercent,
    });
  };

  return (
    <>
      <HeadlineCard
        accent={accent}
        score={credPercent}
        scoreLabel={t('analysis.claims.credibleSuffix')}
        eyebrow={t('analysis.claims.verification')}
        icon={Icon}
        category={result.interpretation || t('analysis.claims.analysisFallback')}
        descriptor={
          <span>
            {result.total_claims} {t('analysis.claims.claim', { count: result.total_claims })}
          </span>
        }
        tldr={buildTldr()}
      />

      {result.total_claims > 0 && (
        <StatBlock>
          <ChipsRow>
            {result.verified_true > 0 && (
              <Chip variant="success">
                {result.verified_true} {t('analysis.claims.chipVerifiedTrue')}
              </Chip>
            )}
            {result.verified_false > 0 && (
              <Chip variant="critical">
                {result.verified_false} {t('analysis.claims.chipVerifiedFalse')}
              </Chip>
            )}
            {result.unverified > 0 && (
              <Chip variant="neutral">
                {result.unverified} {t('analysis.claims.chipUnverified')}
              </Chip>
            )}
            {result.opinions > 0 && (
              <Chip variant="info">
                {result.opinions} {t('analysis.claims.chipOpinions')}
              </Chip>
            )}
            {result.web_searches_made > 0 && (
              <Chip variant="violet">
                <Search size={12} strokeWidth={2} />
                {result.web_searches_made} {t('analysis.claims.chipWebSearches')}
              </Chip>
            )}
            {result.total_duration_ms > 0 && (
              <Chip variant="neutral">
                <Timer size={12} strokeWidth={2} />
                {(result.total_duration_ms / 1000).toFixed(1)}s
              </Chip>
            )}
          </ChipsRow>
        </StatBlock>
      )}

      {renderClaimsEditorial(result, t, expanded, () => setExpanded(o => !o))}
    </>
  );
};
